import { useEffect, useMemo, useState } from 'react';
import type { LoanReconciliationResult, ReconciliationFinding } from './loan-financial.types';
import { formatMoneyAr } from './loan-financial.format';
import {
  findingCategoryGroup,
  findingExplanation,
  findingTitle,
  reconciliationStatusUi,
} from './loan-financial.labels';
import { SectionCard, SkeletonBlock } from './LoanUiBits';
import { apiService } from '../../services/api.service';

type Props = {
  loanId: string;
  /** Resumen FAST desde snapshot (carga inicial). */
  fastStatus?: string | null;
  fastFindingsCount?: number;
  canLoadFull: boolean;
};

type Filter = 'ALL' | 'ERROR' | 'WARNING' | 'INFO';

export function LoanReconciliationPanel({
  loanId,
  fastStatus,
  fastFindingsCount,
  canLoadFull,
}: Props) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState<LoanReconciliationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('ALL');

  const summaryUi = reconciliationStatusUi(full?.status ?? fastStatus);

  useEffect(() => {
    if (!open || !canLoadFull) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getLoanReconciliation(loanId, { mode: 'full' });
        if (!cancelled) setFull(data);
      } catch {
        if (!cancelled) {
          setError('No se pudo verificar la conciliación en este momento.');
          setFull(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, canLoadFull, loanId]);

  const findings = useMemo(() => {
    const list = full?.findings ?? [];
    if (filter === 'ALL') return list;
    if (filter === 'ERROR') {
      return list.filter((f) => f.severity === 'ERROR' || f.severity === 'CRITICAL');
    }
    if (filter === 'WARNING') return list.filter((f) => f.severity === 'WARNING');
    return list.filter((f) => f.severity === 'INFO');
  }, [full, filter]);

  const grouped = useMemo(() => {
    const map = new Map<string, ReconciliationFinding[]>();
    for (const f of findings) {
      const g = findingCategoryGroup(f.category);
      const arr = map.get(g) ?? [];
      arr.push(f);
      map.set(g, arr);
    }
    return [...map.entries()];
  }, [findings]);

  const toneClass =
    summaryUi.tone === 'ok'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : summaryUi.tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : summaryUi.tone === 'error'
          ? 'border-red-200 bg-red-50 text-red-900'
          : 'border-slate-200 bg-slate-50 text-slate-800';

  return (
    <SectionCard title="Conciliación">
      <div className={`rounded-md border px-3 py-2 text-sm ${toneClass}`}>
        <span aria-hidden className="mr-1">
          {summaryUi.icon}
        </span>
        {summaryUi.label}
        {typeof fastFindingsCount === 'number' && !full ? (
          <span className="ml-2 text-xs opacity-80">
            ({fastFindingsCount} hallazgo{fastFindingsCount === 1 ? '' : 's'} en revisión rápida)
          </span>
        ) : null}
        {full?.summary ? (
          <span className="ml-2 text-xs opacity-80">
            {full.summary.errors + full.summary.criticals} errores · {full.summary.warnings}{' '}
            advertencias · {full.summary.infos} info
          </span>
        ) : null}
      </div>

      {canLoadFull ? (
        <button
          type="button"
          className="mt-3 text-sm text-blue-700 hover:underline"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
      ) : (
        <p className="mt-2 text-xs text-slate-500">Sin permiso para ver conciliación detallada.</p>
      )}

      {open ? (
        <div className="mt-3 space-y-3">
          {loading ? <SkeletonBlock className="h-24" /> : null}
          {error ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {error}
              <button
                type="button"
                className="ml-2 underline"
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => setOpen(true), 0);
                }}
              >
                Reintentar
              </button>
            </div>
          ) : null}

          {full && !loading ? (
            <>
              {full.findings.length === 0 ? (
                <p className="text-sm text-emerald-800">Conciliación sin diferencias.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {(
                      [
                        ['ALL', 'Todos'],
                        ['ERROR', 'Errores'],
                        ['WARNING', 'Advertencias'],
                        ['INFO', 'Información'],
                      ] as const
                    ).map(([k, label]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setFilter(k)}
                        className={`rounded-full px-2.5 py-1 border ${
                          filter === k
                            ? 'border-slate-800 bg-slate-800 text-white'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {grouped.map(([group, items]) => (
                    <div key={group}>
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {group}
                      </h3>
                      <ul className="space-y-2">
                        {items.map((f, idx) => (
                          <FindingCard key={`${f.code}-${idx}`} finding={f} />
                        ))}
                      </ul>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

function FindingCard({ finding }: { finding: ReconciliationFinding }) {
  const sev = finding.severity;
  const badge =
    sev === 'CRITICAL' || sev === 'ERROR'
      ? 'bg-red-100 text-red-800'
      : sev === 'WARNING'
        ? 'bg-amber-100 text-amber-900'
        : 'bg-slate-100 text-slate-700';

  return (
    <li className="rounded-md border border-slate-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${badge}`}>
          {sev === 'WARNING' ? 'Requiere revisión' : sev === 'INFO' ? 'Información' : 'Inconsistencia'}
        </span>
        <span className="font-medium text-slate-900">{findingTitle(finding)}</span>
      </div>
      <p className="text-xs text-slate-600 mb-2">{findingExplanation(finding)}</p>
      {(finding.expected != null || finding.actual != null) && (
        <div className="grid grid-cols-3 gap-2 text-xs tabular-nums mb-2">
          {finding.expected != null ? (
            <div>
              <p className="text-slate-500">Esperado</p>
              <p>{formatMoneyAr(finding.expected)}</p>
            </div>
          ) : null}
          {finding.actual != null ? (
            <div>
              <p className="text-slate-500">Registrado</p>
              <p>{formatMoneyAr(finding.actual)}</p>
            </div>
          ) : null}
          {finding.difference != null ? (
            <div>
              <p className="text-slate-500">Diferencia</p>
              <p>{formatMoneyAr(finding.difference)}</p>
            </div>
          ) : null}
        </div>
      )}
      <p className="text-[11px] text-slate-400">Código técnico: {finding.code}</p>
      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        {finding.journalEntryId ? (
          <span className="text-blue-700">Ver asiento · {finding.journalEntryId.slice(0, 8)}…</span>
        ) : null}
        {finding.paymentId ? (
          <span className="text-blue-700">Ver pago · {finding.paymentId.slice(0, 8)}…</span>
        ) : null}
        {finding.installmentId ? (
          <span className="text-blue-700">Ver cuota · {finding.installmentId.slice(0, 8)}…</span>
        ) : null}
      </div>
    </li>
  );
}
